import { c as _c } from "react/compiler-runtime";
import { useCallback, useMemo } from 'react';
import { findKeyAtBackPosition, findKeyAtPosition } from '@/lib/piano/keyboard/keyboard-geometry';
import type { Key } from '@/lib/piano/keyboard/key';
import { cn } from '@/lib/utils';
import { computeKeyboardDrawing, type KeyboardDrawingOptions } from './keyboard-drawing';
import { KeyboardLabelRow, type PianoKeyboardLabel } from './KeyboardLabelRow';
import { KeyboardPlate, keybedHeight, type PianoKeyTone } from './KeyboardPlate';
export interface PianoKeyboardProps {
  className?: string;
  /** Read out to screen readers; the drawing itself carries no text. */
  description: string;
  geometry?: KeyboardDrawingOptions;
  /** The key being read right now, painted brass wherever it sits. */
  highlightedKey?: number | null;
  keys: readonly Key[];
  labels?: readonly PianoKeyboardLabel[];
  onKeyHover?: (keyNumber: number | null) => void;
  onKeySelect?: (keyNumber: number) => void;
  toneByKeyNumber?: ReadonlyMap<number, PianoKeyTone>;
}

/**
 * The keyboard of the instrument, drawn to scale.
 *
 * It appears as a figure of its own on the piano and home pages, and as the
 * x axis under every chart, so that a reader finds a note by looking at the
 * keyboard rather than by reading an axis label.
 */
export const PianoKeyboard = t0 => {
  const $ = _c(40);
  const {
    className,
    description,
    geometry: geometryOptions,
    highlightedKey,
    keys,
    labels,
    onKeyHover,
    onKeySelect,
    toneByKeyNumber
  } = t0;
  let t1;
  if ($[0] !== geometryOptions || $[1] !== keys) {
    t1 = computeKeyboardDrawing(keys, geometryOptions);
    $[0] = geometryOptions;
    $[1] = keys;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const geometry = t1;
  const height = keybedHeight(geometry);
  let t2;
  if ($[3] !== geometry || $[4] !== height) {
    t2 = event => {
      const bounds = event.currentTarget.getBoundingClientRect();
      if (bounds.width === 0 || bounds.height === 0) {
        return null;
      }
      const x = (event.clientX - bounds.left) / bounds.width * geometry.width;
      const y = (event.clientY - bounds.top) / bounds.height * height;
      return y < geometry.leverHeight ? findKeyAtBackPosition(geometry, x) : findKeyAtPosition(geometry, x);
    };
    $[3] = geometry;
    $[4] = height;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const keyAtEvent = t2;
  let t3;
  if ($[6] !== keyAtEvent || $[7] !== onKeyHover) {
    t3 = event_0 => {
      onKeyHover?.(keyAtEvent(event_0));
    };
    $[6] = keyAtEvent;
    $[7] = onKeyHover;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const handleMouseMove = t3;
  let t4;
  if ($[9] !== onKeyHover) {
    t4 = () => {
      onKeyHover?.(null);
    };
    $[9] = onKeyHover;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const handleMouseLeave = t4;
  let t5;
  if ($[11] !== keyAtEvent || $[12] !== onKeySelect) {
    t5 = event_1 => {
      const keyNumber = keyAtEvent(event_1);
      if (keyNumber !== null) {
        onKeySelect?.(keyNumber);
      }
    };
    $[11] = keyAtEvent;
    $[12] = onKeySelect;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const handleClick = t5;
  let t6;
  if ($[14] !== geometry.keysByNumber || $[15] !== geometry.width) {
    t6 = keyNumber_0 => (geometry.keysByNumber.get(keyNumber_0)?.center ?? 0) / geometry.width;
    $[14] = geometry.keysByNumber;
    $[15] = geometry.width;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const placeOf = t6;
  let t7;
  if ($[17] !== className) {
    t7 = cn("w-full", className);
    $[17] = className;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const t8 = `0 0 ${geometry.width} ${height}`;
  const t9 = onKeySelect && "cursor-pointer";
  let t10;
  if ($[19] !== t9) {
    t10 = cn("block h-auto w-full", t9);
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const t11 = onKeyHover ? handleMouseMove : undefined;
  const t12 = onKeyHover ? handleMouseLeave : undefined;
  const t13 = onKeySelect ? handleClick : undefined;
  let t14;
  if ($[21] !== geometry || $[22] !== highlightedKey || $[23] !== toneByKeyNumber) {
    t14 = <KeyboardPlate geometry={geometry} highlightedKey={highlightedKey} toneByKeyNumber={toneByKeyNumber} />;
    $[21] = geometry;
    $[22] = highlightedKey;
    $[23] = toneByKeyNumber;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== description || $[26] !== t10 || $[27] !== t11 || $[28] !== t12 || $[29] !== t13 || $[30] !== t14 || $[31] !== t8) {
    t15 = <svg viewBox={t8} className={t10} role="img" aria-label={description} onMouseMove={t11} onMouseLeave={t12} onClick={t13}>{t14}</svg>;
    $[25] = description;
    $[26] = t10;
    $[27] = t11;
    $[28] = t12;
    $[29] = t13;
    $[30] = t14;
    $[31] = t8;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== labels || $[34] !== placeOf) {
    t16 = labels?.length ? <KeyboardLabelRow labels={labels} placeOf={placeOf} /> : null;
    $[33] = labels;
    $[34] = placeOf;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== t15 || $[37] !== t16 || $[38] !== t7) {
    t17 = <div className={t7}>{t15}{t16}</div>;
    $[36] = t15;
    $[37] = t16;
    $[38] = t7;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  return t17;
};